import React, { useEffect, useState, useContext } from "react";
import {
  Container,
  Card,
  Badge,
  Button,
  Row,
  Col,
  Alert,
} from "react-bootstrap";
import { useParams } from "react-router-dom";
import SimpleHeader from "components/Headers/SimpleHeader.js";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
import { useNavigate } from "react-router-dom";


const EmailQueueView = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const { id } = useParams();

  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const fetchEmail = async () => {
    setLoading(true);
    setError(null);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      let email = null;
      if (id !== "abcdefg") {

          const res = await fetch(`${serverHost}/get_email_queue/${encodeURIComponent(id)}`)
          if (!res.ok)
            throw new Error(
          res.status === 404 ? "Row not found" : "Failed to fetch row"
        );
        const data = await res.json();
        if (!data?.result) throw new Error("Row not found");
        email = data.result;
      } else {
        email = {
          email_queue_uuid: "123e4567-e89b-12d3-a456-426614174000",
          email_date_formatted: "2024-12-16",
          email_time_formatted: "14:32:15",
          email_from: "noreply@example.com",
          email_to: "user@domain.com",
          email_subject: "Test Email: System Notification",
          email_body:
            "This is a test email to verify the email queue system is working correctly. The email contains important information about your account status and recent activity.\n\nPlease review the details below:\n- Account Status: Active\n- Last Login: 2024-12-15\n- Pending Actions: None\n\nThank you for using our service.",
          email_status: "sent",
          email_action_after: "send_welcome_email",
          hostname: "mail.example.com",
        };
      }

      setEmail(email);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRetryEmail = async () => {
    setRetrying(true);
    try {
      // In real implementation:
      // const response = await fetch(`${serverHost}/retry_email/${id}`, {
      //   method: "POST"
      // });
      // if (!response.ok) throw new Error("Failed to retry email");

      await new Promise((resolve) => setTimeout(resolve, 1000));
      console.log(`Email ${id} retry initiated`);
      fetchEmail();
    } catch (err) {
      setError(err.message);
    } finally {
      setRetrying(false);
    }
  };

  useEffect(() => {
    fetchEmail();
  }, []);

  const handleCancel = () => {
    navigate("/admin/hucs/email-queue"); // Navigate back to call flows list
  };

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === "sent") return <Badge bg="success">Sent</Badge>;
    if (statusLower === "waiting") return <Badge bg="info">Waiting</Badge>;
    if (statusLower === "trying")
      return (
        <Badge bg="warning" text="dark">
          Trying
        </Badge>
      );
    if (statusLower === "failed") return <Badge bg="danger">Failed</Badge>;
    return <Badge bg="secondary">{status || "Unknown"}</Badge>;
  };

  if (loading) {
    return (
      <>
        <SimpleHeader name="Email Details" parentName="Email Queue" />
        <Container fluid className="mt--6">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
            <p className="mt-3">Loading email details...</p>
          </div>
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <SimpleHeader name="Email Details" parentName="Email Queue" />
        <Container fluid className="mt--6">
          <Alert variant="danger" className="m-3">
            {error}
          </Alert>
        </Container>
      </>
    );
  }

  if (!email) {
    return (
      <>
        <SimpleHeader name="Email Details" parentName="Email Queue" />
        <Container fluid className="mt--6">
          <Alert variant="warning" className="m-3">
            Email not found
          </Alert>
        </Container>
      </>
    );
  }

  return (
    <>
      <SimpleHeader name="Email Details" parentName="Email Queue" />

      <Container fluid className="mt--6">
        <Card
          className={`shadow-sm ${
            isDark ? "bg-dark text-light border-dark" : ""
          }`}
        >
          <Card.Header
            style={{
              backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
              color: isDark ? "#e2e8f0" : "#111",
            }}
          >
            <h4 className="mb-0">Email Details</h4>
          </Card.Header>

          <Card.Body
            style={{
              backgroundColor: isDark ? "#1f2937" : "#fff",
              color: isDark ? "#e2e8f0" : "#212529",
            }}
          >
            {/* Date, From, To - Single Line */}
            <Row
              className="mb-3 pb-3 border-bottom"
              style={{ borderColor: isDark ? "#374151" : "#dee2e6" }}
            >
              <Col md={3}>
                <div>
                  <strong
                    style={{
                      color: isDark ? "#9ca3af" : "#6c757d",
                      fontSize: "0.875rem",
                    }}
                  >
                    DATE
                  </strong>
                  <div className="mt-1" style={{ fontSize: "1rem" }}>
                    {email.email_date_formatted} {email.email_time_formatted}
                  </div>
                </div>
              </Col>
              <Col md={4}>
                <div>
                  <strong
                    style={{
                      color: isDark ? "#9ca3af" : "#6c757d",
                      fontSize: "0.875rem",
                    }}
                  >
                    FROM
                  </strong>
                  <div className="mt-1" style={{ fontSize: "1rem" }}>
                    {email.email_from}
                  </div>
                </div>
              </Col>
              <Col md={5}>
                <div>
                  <strong
                    style={{
                      color: isDark ? "#9ca3af" : "#6c757d",
                      fontSize: "0.875rem",
                    }}
                  >
                    TO
                  </strong>
                  <div className="mt-1" style={{ fontSize: "1rem" }}>
                    {email.email_to}
                  </div>
                </div>
              </Col>
            </Row>

            {/* Subject - Next Line */}
            <Row
              className="mb-3 pb-3 border-bottom"
              style={{ borderColor: isDark ? "#374151" : "#dee2e6" }}
            >
              <Col>
                <div>
                  <strong
                    style={{
                      color: isDark ? "#9ca3af" : "#6c757d",
                      fontSize: "0.875rem",
                    }}
                  >
                    SUBJECT
                  </strong>
                  <div
                    className="mt-1"
                    style={{ fontSize: "1rem", fontWeight: 500 }}
                  >
                    {email.email_subject}
                  </div>
                </div>
              </Col>
            </Row>

            {/* Body - Below Subject */}
            <Row
              className="mb-3 pb-3 border-bottom"
              style={{ borderColor: isDark ? "#374151" : "#dee2e6" }}
            >
              <Col>
                <div>
                  <strong
                    style={{
                      color: isDark ? "#9ca3af" : "#6c757d",
                      fontSize: "0.875rem",
                    }}
                  >
                    BODY
                  </strong>
                  <div
                    className="mt-2 p-3"
                    style={{
                      backgroundColor: isDark ? "#374151" : "#f8f9fa",
                      borderRadius: "4px",
                      whiteSpace: "pre-wrap",
                      fontSize: "0.95rem",
                      lineHeight: "1.6",
                      maxHeight: "400px",
                      overflowY: "auto",
                    }}
                  >
                    {email.email_body}
                  </div>
                </div>
              </Col>
            </Row>

            {/* Status, Retry, After Email - Single Line */}
            <Row className="align-items-center">
              <Col md={3}>
                <div>
                  <strong
                    style={{
                      color: isDark ? "#9ca3af" : "#6c757d",
                      fontSize: "0.875rem",
                    }}
                  >
                    STATUS
                  </strong>
                  <div className="mt-1">
                    {getStatusBadge(email.email_status)}
                  </div>
                </div>
              </Col>
              <Col md={3}>
                <div>
                  <strong
                    style={{
                      color: isDark ? "#9ca3af" : "#6c757d",
                      fontSize: "0.875rem",
                    }}
                  >
                    RETRY
                  </strong>
                  <div className="mt-1">
                    {email.email_status?.toLowerCase() === "failed" ||
                    email.email_status?.toLowerCase() === "trying" ? (
                      <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={handleRetryEmail}
                        disabled={retrying}
                      >
                        {retrying ? "Retrying..." : "Retry"}
                      </Button>
                    ) : (
                      <span style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
                        -
                      </span>
                    )}
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div>
                  <strong
                    style={{
                      color: isDark ? "#9ca3af" : "#6c757d",
                      fontSize: "0.875rem",
                    }}
                  >
                    AFTER EMAIL
                  </strong>
                  <div className="mt-1" style={{ fontSize: "1rem" }}>
                    {email.email_action_after || "-"}
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
          <Card.Footer
            style={{
              backgroundColor: isDark ? "#1F1F1F" : "#fff",
              color: isDark ? "#e2e8f0" : "#212529",
            }}
          >
            <div className="d-flex justify-content-between mt-4">
              <Button variant="light" onClick={handleCancel}>
                Back
              </Button>
            </div>
          </Card.Footer>
        </Card>
      </Container>
    </>
  );
};

export default EmailQueueView;
